import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { MapPin, Filter } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusBadge } from "../components/Layout";
import { Badge } from "../components/ui/badge";

// Custom pin icon (blue circle)
const pinIcon = (color = "#2563EB") => L.divIcon({
  className: "lom-pin",
  html: `<div style="background:${color};width:24px;height:24px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 3px 8px rgba(0,0,0,.25)"><div style="width:8px;height:8px;background:white;border-radius:50%;position:absolute;top:6px;left:6px"></div></div>`,
  iconSize: [24, 24], iconAnchor: [12, 24], popupAnchor: [0, -22],
});

const STATUS_COLOR = { off_market: "#2563EB", pre_market: "#F59E0B", on_market: "#10B981", sold: "#64748B" };

// Rough centroid fallback per Italian region (very approximate) so properties without lat/lng still show
const CITY_COORDS = {
  roma: [41.9028, 12.4964], milano: [45.4642, 9.19], torino: [45.0703, 7.6869],
  firenze: [43.7696, 11.2558], napoli: [40.8518, 14.2681], bologna: [44.4949, 11.3426],
  venezia: [45.4408, 12.3155], genova: [44.4056, 8.9463], bari: [41.1171, 16.8719],
  palermo: [38.1157, 13.3615], verona: [45.4384, 10.9916], padova: [45.4064, 11.8768],
  cagliari: [39.2238, 9.1217], catania: [37.5079, 15.083],
};

function coordsFor(p) {
  if (typeof p.latitude === "number" && typeof p.longitude === "number") return [p.latitude, p.longitude];
  const key = (p.city || "").toLowerCase().trim();
  if (CITY_COORDS[key]) return CITY_COORDS[key];
  return null;
}

export default function MapPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");

  useEffect(() => {
    const params = status !== "all" ? { status } : {};
    api.get("/properties", { params }).then(({ data }) => setItems(data));
  }, [status]);

  const mapped = useMemo(() => items.map((p) => ({ p, coords: coordsFor(p) })).filter((x) => x.coords), [items]);
  const center = mapped.length ? mapped[0].coords : [41.8719, 12.5674]; // Italy default

  return (
    <div className="space-y-6" data-testid="map-page">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Mappa immobili</h1>
          <p className="text-muted-foreground text-sm mt-1">Visualizza gli immobili per area geografica. {items.length - mapped.length > 0 && `${items.length - mapped.length} immobili senza coordinate.`}</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-56" data-testid="map-status-filter"><Filter className="w-3.5 h-3.5 mr-2" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="off_market">Off market</SelectItem>
            <SelectItem value="pre_market">Pre-market</SelectItem>
            <SelectItem value="on_market">On market</SelectItem>
            <SelectItem value="sold">Venduto</SelectItem>
          </SelectContent>
        </Select>
      </header>

      <Card className="border-border/60 overflow-hidden p-0">
        <div className="h-[560px] w-full">
          <MapContainer center={center} zoom={mapped.length ? 6 : 5} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {mapped.map(({ p, coords }) => (
              <Marker key={p.id} position={coords} icon={pinIcon(STATUS_COLOR[p.status] || "#2563EB")}>
                <Popup>
                  <div style={{ minWidth: 220 }}>
                    <div className="font-medium text-sm">{p.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={12} />{p.address}, {p.city}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <StatusBadge status={p.status} />
                      <span className="text-xs tabular-nums font-semibold">€ {Intl.NumberFormat("it-IT").format(p.price || 0)}</span>
                    </div>
                    <Link to={`/properties/${p.id}`} className="text-primary text-xs mt-2 inline-block hover:underline">Apri scheda →</Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </Card>

      <Card className="border-border/60 p-4">
        <div className="flex flex-wrap gap-4 text-xs">
          {Object.entries(STATUS_COLOR).map(([k, c]) => (
            <div key={k} className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: c }} />
              <span className="capitalize text-muted-foreground">{k.replace("_", " ")}</span>
            </div>
          ))}
          <Badge variant="secondary" className="ml-auto">{mapped.length} immobili in mappa</Badge>
        </div>
      </Card>
    </div>
  );
}
